import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { 
  FiMessageCircle, FiVideo, FiUsers, FiHeart, FiCheck, FiX, 
  FiStar, FiArrowRight, FiPhoneCall, FiZap, FiShield,
  FiHeadphones, FiClock, FiTrendingUp, FiSmile, FiMail, FiPhone
} = FiIcons;

function App() {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const handleUpgradeClick = () => {
    window.open('https://www.funnelsdone.com/product-details/product/691d77c60f490b708ae9b197/?variant=691d784f0f490b6f16e9ffdd', '_blank');
  };

  const handleMonthlyClick = () => {
    window.open('https://www.funnelsdone.com/product-details/product/691d77c60f490b708ae9b197/?variant=691d77c60f490bd9a2e9b1a6', '_blank');
  };

  const handleYearlyClick = () => {
    window.open('https://www.funnelsdone.com/product-details/product/691d77c60f490b708ae9b197/?variant=691d77c60f490b7fbae9b1ab', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Sticky Header */}
      <motion.div 
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isSticky ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">FD</span>
            </div>
            <span className="font-semibold text-gray-800">FunnelsDone</span>
          </div>
          <button 
            onClick={handleUpgradeClick}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300"
          >
            Upgrade Now
          </button>
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Upgrade to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Premium Priority Support</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              Stay Fully Supported, Never Feel Stuck Again.
            </p>
            <p className="text-lg text-gray-700 mb-12 max-w-3xl mx-auto">
              Direct Access to Abdul & His Expert Team + Instant WhatsApp/Discord Support + 1-to-1 Zoom Guidance.
            </p>
          </motion.div>

          <motion.div 
            className="relative mb-16"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-3xl p-8 md:p-16 shadow-2xl">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mb-4">
                    <SafeIcon icon={FiUsers} className="text-2xl text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Expert Team</h3>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                    <SafeIcon icon={FiMessageCircle} className="text-2xl text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Instant Support</h3>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                    <SafeIcon icon={FiVideo} className="text-2xl text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800">1-to-1 Guidance</h3>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <button 
              onClick={handleUpgradeClick}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Upgrade to Premium Priority Support
            </button>
            <button 
              onClick={handleUpgradeClick}
              className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-50 transition-all duration-300"
            >
              Learn More
            </button>
          </motion.div>
        </div>
      </section>

      {/* Emotional Story Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              A Personal Message from Our Heart
            </h2>
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiHeart} className="text-2xl text-white" />
                </div>
              </div>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                "For the past 3 years, we noticed one thing… Our Platinum members don't want to walk alone after the first 3 months. They love having us close — inside WhatsApp, inside Discord, helping instantly, resolving doubts, and guiding them like family.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-semibold">
                That's why we created Premium Priority Support — so you always have someone by your side, every single day."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Special Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
            {...fadeInUp}
          >
            Why This Support is Special
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiSmile,
                title: "You're Never Alone Again",
                description: "Real humans. Real experts. Replies that care.",
                gradient: "from-blue-500 to-indigo-500"
              },
              {
                icon: FiPhoneCall,
                title: "Abdul + Team Inside Your Pocket",
                description: "Instant WhatsApp or Discord support whenever you feel stuck.",
                gradient: "from-green-500 to-emerald-500"
              },
              {
                icon: FiShield,
                title: "Peace of Mind Every Day",
                description: "Zoom calls, handholding, and expert guidance without waiting 24 hours.",
                gradient: "from-purple-500 to-pink-500"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${item.gradient} rounded-full flex items-center justify-center mb-6`}>
                  <SafeIcon icon={item.icon} className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What You Get
            </h2>
            <p className="text-xl text-gray-600">Included in Premium Priority Support</p>
          </motion.div>

          <motion.div 
            className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="grid gap-6">
              {[
                "Direct WhatsApp/Discord support with Abdul & Expert Team",
                "Instant replies for urgent doubts",
                "1-to-1 Zoom support calls (as required)",
                "Technical guidance for setups, funnels, automations",
                "Strategy support + mindset support",
                "Continuation after first 3 months of DWY",
                "Priority attention during launches, campaigns & course setup",
                "Option to choose Monthly or Yearly plan",
                "Email Escalation for Ticketing to support@yinguru.com",
                "WhatsApp/Call support on +919884899080"
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="text-white text-sm" />
                  </div>
                  <p className="text-gray-700 text-lg">{feature}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-6 bg-gradient-to-r from-orange-100 to-amber-100 rounded-2xl text-center">
              <p className="text-lg font-semibold text-gray-800 italic">
                "This is not just support. This is partnership."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
            {...fadeInUp}
          >
            Priority vs Premium Comparison
          </motion.h2>

          <motion.div 
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-blue-50">
                    <th className="text-left p-6 font-semibold text-gray-800">Feature</th>
                    <th className="text-center p-6 font-semibold text-gray-800">Priority Support (Default)</th>
                    <th className="text-center p-6 font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600">Premium Priority Support</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["FD Live Chat 24×7", true, true],
                    ["Zoom Support (on request)", true, true],
                    ["Knowledge Base", true, true],
                    ["Courses + FD University", true, true],
                    ["Daily Live Coaching Calls", true, true],
                    ["Email Escalation to support@yinguru.com", true, true],
                    ["WhatsApp/Call support on +919884899080", true, true],
                    ["Direct WhatsApp/Discord Support", false, true],
                    ["Instant Response from Abdul's Team", false, true],
                    ["1-to-1 Zoom Calls", false, true],
                    ["Personalized Guidance", false, true]
                  ].map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="p-6 font-medium text-gray-800">{row[0]}</td>
                      <td className="p-6 text-center">
                        {row[1] ? (
                          <SafeIcon icon={FiCheck} className="text-green-500 text-xl mx-auto" />
                        ) : (
                          <SafeIcon icon={FiX} className="text-gray-400 text-xl mx-auto" />
                        )}
                      </td>
                      <td className="p-6 text-center bg-gradient-to-r from-blue-50 to-indigo-50">
                        <SafeIcon icon={FiCheck} className="text-blue-600 text-xl mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
            {...fadeInUp}
          >
            What Our Members Say
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Priya Sharma",
                role: "Platinum Member",
                content: "The instant WhatsApp support saved me 10 hours during my product launch. Abdul's team guided me step-by-step through every funnel setup.",
                rating: 5
              },
              {
                name: "Rajesh Kumar",
                role: "Platinum Member", 
                content: "I never felt alone building my course business. The 1-to-1 Zoom calls gave me clarity and confidence I needed to scale to 6 figures.",
                rating: 5
              },
              {
                name: "Anita Patel",
                role: "Platinum Member",
                content: "Premium Priority Support is like having Abdul's entire team in my pocket. Response time is incredible, and solutions are always spot-on.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-xl"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-lg" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-blue-600 text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
            {...fadeInUp}
          >
            Choose Your Plan
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-200"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Monthly Plan</h3>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span>Private WhatsApp/Discord</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span>Instant Support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span>1:1 Zoom Assistance</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiMail} className="text-green-500" />
                  <span>Email Escalation Support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="text-green-500" />
                  <span>WhatsApp/Call Support</span>
                </li>
              </ul>
              <button 
                onClick={handleMonthlyClick}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
              >
                Upgrade Monthly
              </button>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-8 shadow-2xl text-white relative overflow-hidden"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="absolute top-4 right-4 bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-sm font-semibold">
                Popular
              </div>
              <h3 className="text-2xl font-bold mb-4">Yearly Plan</h3>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-white" />
                  <span>Everything Monthly</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-white" />
                  <span>Priority onboarding</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-white" />
                  <span>Bonus strategy call</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiMail} className="text-white" />
                  <span>Email Escalation Support</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="text-white" />
                  <span>WhatsApp/Call Support</span>
                </li>
              </ul>
              <button 
                onClick={handleYearlyClick}
                className="w-full bg-white text-blue-600 py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
              >
                Upgrade Yearly & Save More
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
            {...fadeInUp}
          >
            Multiple Ways to Reach Us
          </motion.h2>

          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiMail} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Email Support</h3>
              <p className="text-gray-600 mb-4">For detailed ticketing and escalation</p>
              <a 
                href="mailto:support@yinguru.com"
                className="text-blue-600 font-semibold text-lg hover:text-blue-700 transition-colors"
              >
                support@yinguru.com
              </a>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiPhone} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">WhatsApp & Call</h3>
              <p className="text-gray-600 mb-4">For urgent support and quick assistance</p>
              <a 
                href="tel:+919884899080"
                className="text-green-600 font-semibold text-lg hover:text-green-700 transition-colors"
              >
                +91 98848 99080
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Upgrade Matters */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-16"
            {...fadeInUp}
          >
            Why Upgrade Matters
          </motion.h2>

          <motion.div 
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="space-y-6">
              {[
                "When you're building funnels, speed matters.",
                "When you're stuck at midnight, guidance matters.",
                "When you're launching, support matters.",
                "When you're scaling, accountability matters.",
                "When you dream big, the right team matters."
              ].map((point, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiArrowRight} className="text-white text-sm" />
                  </div>
                  <p className="text-lg text-gray-700 text-left">{point}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
            {...fadeInUp}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-6">
            {[
              {
                question: "What happens after my first 3 free months?",
                answer: "After your first 3 months of Done-With-You support, you can choose to upgrade to Premium Priority Support to continue receiving personalized guidance and instant access to our expert team."
              },
              {
                question: "Is this mandatory for Platinum members?",
                answer: "No, Premium Priority Support is completely optional. You'll still have access to all your Platinum benefits, but this upgrade gives you direct access to Abdul's team for instant, personalized support."
              },
              {
                question: "Is support really on WhatsApp/Discord?",
                answer: "Yes! You'll get direct access to our private WhatsApp group and Discord server where Abdul and his expert team provide instant support, guidance, and answers to your questions."
              },
              {
                question: "Can I switch from monthly to yearly later?",
                answer: "Absolutely! You can upgrade from monthly to yearly at any time. We'll prorate your current month and apply it to your yearly subscription."
              },
              {
                question: "Will Abdul personally be inside the group?",
                answer: "Yes, Abdul is actively involved in the Premium Priority Support channels, along with his handpicked expert team members who are trained to provide the same level of guidance and support."
              },
              {
                question: "What if I cancel later?",
                answer: "You can cancel anytime with no hassles. Your support access will continue until the end of your current billing period, and you'll still retain all your original Platinum member benefits."
              },
              {
                question: "How quickly will I get email support responses?",
                answer: "Email escalation to support@yinguru.com is available for both Priority and Premium members. Premium members receive priority handling and faster response times."
              },
              {
                question: "Can I really call or WhatsApp the support number?",
                answer: "Yes! The number +91 98848 99080 is available for both Priority and Premium members for WhatsApp messages and calls. Premium members get priority handling and faster response."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Let's Continue the Journey Together
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
              Upgrade to Premium Priority Support and get the support you deserve.
            </p>
            <button 
              onClick={handleUpgradeClick}
              className="bg-white text-blue-600 px-12 py-6 rounded-full text-xl font-bold hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 mb-8"
            >
              Upgrade Now & Stay Supported
            </button>
            <div className="flex items-center justify-center space-x-2 text-blue-100">
              <SafeIcon icon={FiShield} className="text-lg" />
              <span>Trusted by 2,500+ Members Inside FunnelsDone</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 md:hidden">
        <button 
          onClick={handleUpgradeClick}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-full font-semibold text-lg"
        >
          Upgrade to Premium Priority Support
        </button>
      </div>
    </div>
  );
}

export default App;